import { ScheduleInformation, TimeSlot } from '../graph/types';
import { addMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  formatTime,
  formatDateLabel,
  getBusinessDays,
  createDateInTimezone,
} from '../utils/dateUtils';

export interface AvailabilityParams {
  schedules: ScheduleInformation[];
  startDate: Date;
  endDate: Date;
  durationMinutes: number;
  intervalMinutes: number;
  workingHoursStart: string; // "HH:mm" format
  workingHoursEnd: string;   // "HH:mm" format
  timeZone: string;
  maxResults?: number;
}

/**
 * Find common available time slots for all attendees
 */
export function findCommonTimeSlots(params: AvailabilityParams): TimeSlot[] {
  const {
    schedules,
    startDate,
    endDate,
    durationMinutes,
    intervalMinutes,
    workingHoursStart,
    workingHoursEnd,
    timeZone,
    maxResults = 8,
  } = params;

  if (schedules.length === 0) {
    return [];
  }

  // Reset debug counter for fresh logging
  resetDebugCounter();

  // Get all business days in the range
  const businessDays = getBusinessDays(startDate, endDate);
  const commonSlots: TimeSlot[] = [];

  // Get current time plus 5 minutes buffer
  const minimumStartTime = addMinutes(new Date(), 5);
  console.log('=== FIND TIME v2.1 - Enhanced Debugging ===');
  console.log('Minimum start time (now + 5 min):', minimumStartTime.toISOString());
  console.log('Searching business days:', businessDays.length);

  // Debug: Log schedule data
  console.log('Schedule data for attendees:');
  schedules.forEach((schedule, idx) => {
    console.log(`  Attendee ${idx + 1} (${schedule.scheduleId}):`);
    console.log(`    availabilityView length: ${schedule.availabilityView?.length ?? 'undefined'}`);
    console.log(`    availabilityView: "${schedule.availabilityView}"`);
    // Count free slots (0s) vs busy slots
    const freeCount = (schedule.availabilityView?.match(/0/g) || []).length;
    const busyCount = (schedule.availabilityView?.length || 0) - freeCount;
    console.log(`    Free slots: ${freeCount}, Busy slots: ${busyCount}`);
  });

  // Calculate the base time for availability view index calculations
  // This should be midnight of the FIRST day in the range (in the selected timezone)
  // because Microsoft Graph returns a single availabilityView covering the entire date range
  const availabilityViewBaseTime = createDateInTimezone(startDate, '00:00', timeZone);
  console.log('=== Base Time Calculation ===');
  console.log('Input startDate:', startDate.toISOString());
  console.log('Input startDate (local):', startDate.toString());
  console.log('TimeZone:', timeZone);
  console.log('Calculated availabilityViewBaseTime:', availabilityViewBaseTime.toISOString());

  // Also log expected index for 9 AM on first business day
  if (businessDays.length > 0) {
    const firstDayNineAM = createDateInTimezone(businessDays[0], '09:00', timeZone);
    const expectedOffset = (firstDayNineAM.getTime() - availabilityViewBaseTime.getTime()) / (1000 * 60);
    const expectedIndex = Math.floor(expectedOffset / intervalMinutes);
    console.log('First business day 9 AM:', firstDayNineAM.toISOString());
    console.log('Expected index for 9 AM:', expectedIndex);
  }

  // For each business day
  for (const day of businessDays) {
    // Create time slots for the day within working hours
    const dayStart = createDateInTimezone(day, workingHoursStart, timeZone);
    const dayEnd = createDateInTimezone(day, workingHoursEnd, timeZone);

    let currentTime = dayStart;

    // Iterate through the day in intervals
    while (currentTime < dayEnd) {
      const slotEnd = addMinutes(currentTime, durationMinutes);

      // Check if the entire slot fits within the working day
      if (slotEnd <= dayEnd) {
        // Check if this slot is free for all attendees
        // Use the base time from the start of the entire range for index calculations
        const isFree = isSlotFreeForAll(currentTime, slotEnd, schedules, intervalMinutes, availabilityViewBaseTime);

        // Only include slots that are at least 5 minutes in the future
        const isInFuture = currentTime >= minimumStartTime;

        // Debug logging for first few slots
        if (commonSlots.length < 3) {
          console.log(`Slot ${formatTime(currentTime, timeZone)}: isFree=${isFree}, isInFuture=${isInFuture}, currentTime=${currentTime.toISOString()}`);
        }

        if (isFree && isInFuture) {
          commonSlots.push({
            start: currentTime,
            end: slotEnd,
            displayStart: formatTime(currentTime, timeZone),
            displayEnd: formatTime(slotEnd, timeZone),
            dateLabel: formatDateLabel(currentTime, timeZone),
          });
        }
      }

      currentTime = addMinutes(currentTime, intervalMinutes);
    }
  }

  console.log('Total individual slots found:', commonSlots.length);

  // Merge consecutive slots into blocks
  const mergedSlots = mergeConsecutiveSlots(commonSlots, timeZone);
  console.log('Merged into', mergedSlots.length, 'time blocks');

  // Apply maxResults limit to the merged blocks
  if (mergedSlots.length > maxResults) {
    return mergedSlots.slice(0, maxResults);
  }

  return mergedSlots;
}

/**
 * Merge consecutive time slots into larger blocks
 * e.g., 9:00-9:30, 9:30-10:00, 10:00-10:30 becomes 9:00-10:30
 */
function mergeConsecutiveSlots(slots: TimeSlot[], timeZone: string): TimeSlot[] {
  if (slots.length === 0) return [];

  const merged: TimeSlot[] = [];
  let currentBlock = { ...slots[0] };

  for (let i = 1; i < slots.length; i++) {
    const slot = slots[i];

    // Check if this slot is consecutive with the current block
    // (same day and starts when current block ends)
    const isSameDay = currentBlock.dateLabel === slot.dateLabel;
    const isConsecutive = currentBlock.end.getTime() === slot.start.getTime();

    if (isSameDay && isConsecutive) {
      // Extend the current block
      currentBlock.end = slot.end;
      currentBlock.displayEnd = formatTime(slot.end, timeZone);
    } else {
      // Save the current block and start a new one
      merged.push(currentBlock);
      currentBlock = { ...slot };
    }
  }

  // Don't forget the last block
  merged.push(currentBlock);

  return merged;
}

/**
 * Check if a time slot is free for all attendees
 */
function isSlotFreeForAll(
  slotStart: Date,
  slotEnd: Date,
  schedules: ScheduleInformation[],
  intervalMinutes: number,
  baseStartTime: Date
): boolean {
  // For each attendee's schedule
  for (const schedule of schedules) {
    if (!isSlotFreeForAttendee(slotStart, slotEnd, schedule, intervalMinutes, baseStartTime)) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a time slot is free for a single attendee
 */
let debugCheckCount = 0;
// Reset debug counter (call this at start of findCommonTimeSlots)
function resetDebugCounter() {
  debugCheckCount = 0;
}
function isSlotFreeForAttendee(
  slotStart: Date,
  slotEnd: Date,
  schedule: ScheduleInformation,
  intervalMinutes: number,
  baseStartTime: Date
): boolean {
  const availabilityView = schedule.availabilityView;

  // Handle missing or empty availabilityView
  if (!availabilityView || availabilityView.length === 0) {
    if (debugCheckCount < 3) {
      console.log(`  WARNING: ${schedule.scheduleId} has no availabilityView data!`);
    }
    return false; // Can't determine availability, assume busy
  }

  // Calculate which indices in the availability view this slot spans
  const startOffset = (slotStart.getTime() - baseStartTime.getTime()) / (1000 * 60);
  const endOffset = (slotEnd.getTime() - baseStartTime.getTime()) / (1000 * 60);

  const startIndex = Math.floor(startOffset / intervalMinutes);
  const endIndex = Math.ceil(endOffset / intervalMinutes);

  // Debug logging for first few checks
  if (debugCheckCount < 5) {
    console.log(`  Checking ${schedule.scheduleId}:`);
    console.log(`    slotStart: ${slotStart.toISOString()}, baseStartTime: ${baseStartTime.toISOString()}`);
    console.log(`    startOffset: ${startOffset} min, endOffset: ${endOffset} min`);
    console.log(`    indices: ${startIndex}-${endIndex}, availabilityView.length: ${availabilityView.length}`);
    console.log(`    chars at indices: "${availabilityView.substring(startIndex, endIndex)}"`);
    debugCheckCount++;
  }

  // Check if indices are valid
  if (startIndex < 0 || startIndex >= availabilityView.length) {
    if (debugCheckCount < 10) {
      console.log(`  WARNING: Index out of bounds! startIndex=${startIndex}, length=${availabilityView.length}`);
    }
    return false;
  }

  // Check all intervals that overlap with this slot
  for (let i = startIndex; i < endIndex && i < availabilityView.length; i++) {
    const status = availabilityView[i];

    // '0' = Free, anything else (1=Tentative, 2=Busy, 3=OOF, 4=Working elsewhere) = not free
    if (status !== '0') {
      return false;
    }
  }

  return true;
}

/**
 * Format time slots for insertion into email (HTML format with bold)
 */
export function formatTimeSlotsForEmail(
  slots: TimeSlot[],
  timeZone: string,
  timeZoneAbbrev: string = 'ET',
  attendees?: Array<{ displayName: string; emailAddress: string }>,
  asHtml: boolean = false
): string {
  if (slots.length === 0) {
    return 'No common available times found.';
  }

  if (asHtml) {
    return formatAsHtml(slots, timeZone, timeZoneAbbrev, attendees);
  }

  const timeZoneFullName = getTimezoneFullName(timeZone);
  const now = new Date();
  const currentDateTime = formatInTimeZone(now, timeZone, 'MMMM d, yyyy \'at\' h:mm a');

  let result = `Here are a few ${timeZoneFullName} (${timeZoneAbbrev}) slots that work for the attendees listed below as of ${currentDateTime} ${timeZoneAbbrev}. Availability is subject to change.\n`;

  // Include attendees list if provided
  if (attendees && attendees.length > 0) {
    result += '\nAttendees:\n';
    for (const attendee of attendees) {
      result += `• ${attendee.displayName} (${attendee.emailAddress})\n`;
    }
    result += '\n'; // Extra line break between attendees and times
  } else {
    result += '\n'; // Space before times if no attendees
  }

  // Group slots by date (in the specified timezone)
  const slotsByDate = new Map<string, TimeSlot[]>();
  for (const slot of slots) {
    const dateKey = formatDateLabel(slot.start, timeZone); // Format in target timezone
    if (!slotsByDate.has(dateKey)) {
      slotsByDate.set(dateKey, []);
    }
    slotsByDate.get(dateKey)!.push(slot);
  }

  // Format each date group
  for (const [_dateLabel, dateSlots] of slotsByDate) {
    // Convert short label to full format: "Mon Jan 12" -> "Monday, January 12"
    const fullDateLabel = formatFullDateLabel(dateSlots[0].start, timeZone);
    result += `${fullDateLabel}:\n`;

    for (const slot of dateSlots) {
      // Re-format times in the specified timezone
      const displayStart = formatTime(slot.start, timeZone);
      const displayEnd = formatTime(slot.end, timeZone);
      result += `• ${displayStart}–${displayEnd}\n`;
    }

    result += '\n';
  }

  return result.trim();
}

/**
 * Format time slots as HTML with bold formatting
 */
function formatAsHtml(
  slots: TimeSlot[],
  timeZone: string,
  timeZoneAbbrev: string,
  attendees?: Array<{ displayName: string; emailAddress: string }>
): string {
  const timeZoneFullName = getTimezoneFullName(timeZone);
  const now = new Date();
  const currentDateTime = formatInTimeZone(now, timeZone, 'MMMM d, yyyy \'at\' h:mm a');

  let html = `<p>Here are a few ${timeZoneFullName} (${timeZoneAbbrev}) slots that work for the attendees listed below as of ${currentDateTime} ${timeZoneAbbrev}. Availability is subject to change.</p><br>`;

  // Include attendees list if provided
  if (attendees && attendees.length > 0) {
    html += '<p><strong>Attendees:</strong></p><ul>';
    for (const attendee of attendees) {
      html += `<li>${attendee.displayName} (${attendee.emailAddress})</li>`;
    }
    html += '</ul><br>'; // Extra line break between attendees and times
  }

  // Group slots by date (in the specified timezone)
  const slotsByDate = new Map<string, TimeSlot[]>();
  for (const slot of slots) {
    const dateKey = formatDateLabel(slot.start, timeZone); // Format in target timezone
    if (!slotsByDate.has(dateKey)) {
      slotsByDate.set(dateKey, []);
    }
    slotsByDate.get(dateKey)!.push(slot);
  }

  // Format each date group
  for (const [_dateLabel, dateSlots] of slotsByDate) {
    const fullDateLabel = formatFullDateLabel(dateSlots[0].start, timeZone);
    html += `<p><strong>${fullDateLabel}:</strong></p><ul>`;

    for (const slot of dateSlots) {
      // Re-format times in the specified timezone
      const displayStart = formatTime(slot.start, timeZone);
      const displayEnd = formatTime(slot.end, timeZone);
      html += `<li>${displayStart}–${displayEnd}</li>`;
    }

    html += '</ul>';
  }

  return html;
}

/**
 * Format full date label (e.g., "Monday, January 12")
 */
function formatFullDateLabel(date: Date, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'EEEE, MMMM d');
}

/**
 * Get timezone abbreviation from timezone string
 */
export function getTimezoneAbbreviation(timezone: string): string {
  const abbreviations: Record<string, string> = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Los_Angeles': 'PT',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Asia/Tokyo': 'JST',
    'Australia/Sydney': 'AEDT',
    'UTC': 'UTC',
  };

  return abbreviations[timezone] || timezone;
}

/**
 * Get full timezone name from timezone string
 */
export function getTimezoneFullName(timezone: string): string {
  const fullNames: Record<string, string> = {
    'America/New_York': 'Eastern Time',
    'America/Chicago': 'Central Time',
    'America/Denver': 'Mountain Time',
    'America/Los_Angeles': 'Pacific Time',
    'Europe/London': 'Greenwich Mean Time',
    'Europe/Paris': 'Central European Time',
    'Asia/Tokyo': 'Japan Standard Time',
    'Australia/Sydney': 'Australian Eastern Daylight Time',
    'UTC': 'Coordinated Universal Time',
  };

  return fullNames[timezone] || timezone;
}
